import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
    GET_CHANNEL_VIDEOS,
    GetChannelVideosData,
    GetChannelVideosVariables,
    ChannelVideo,
    ChannelHomeMedia
} from '@jwp/ott-common/src/graphql/query/channel.queries';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { PERMISSIONS, hasPermission } from '@jwp/ott-common/src/constants';
import env from '@jwp/ott-common/src/env';
import { PATH_MY_VIDEO_DETAILS, PATH_CHENNAL_PREVIEW } from '@jwp/ott-common/src/paths';
import { useUploadStore } from '@jwp/ott-common/src/stores/UploadStore';
import Spinner from '../../components/Spinner/Spinner';
import Icon from '../../components/Icon/Icon';
import Button from '../../components/Button/Button';
import IllustrationIcon from '@jwp/ott-theme/assets/icons/illustration.svg?react';
import MoreIcon from '@jwp/ott-theme/assets/icons/more.svg?react';
import PlayIcon from '@jwp/ott-theme/assets/icons/play.svg?react';
import InfoIcon from '@jwp/ott-theme/assets/icons/info.svg?react';
import styles from './ChannelSearchResults.module.scss';
import { formatTime } from '../../utils/formatTime';
import { formatDurationTag } from '@jwp/ott-common/src/utils/formatting';

const CHANNEL_REELS_PAGE_SIZE = 10;
const CHANNEL_SEARCH_PAGE_SIZE = 12;

interface ChannelSearchResultsProps {
    channelId: string;
    searchQuery: string;
    onMediaMenuClick?: (e: React.MouseEvent, item: ChannelVideo) => void;
    currentLocation?: string;
    normalUser?: boolean;
    isReported?: boolean;
}

const ChannelSearchResults: React.FC<ChannelSearchResultsProps> = ({
    channelId,
    searchQuery,
    onMediaMenuClick,
    currentLocation,
    normalUser,
    isReported: propIsReported
}) => {
    const { t } = useTranslation(['user', 'video']);
    const location = useLocation();
    const navigate = useNavigate();
    const user = useAccountStore((state) => state.user);

    const {
        setPreviewUrl,
        setValues,
        setReturnPath,
    } = useUploadStore();

    const getSearchVariables = (offset = 0): GetChannelVideosVariables => ({
        input: {
            first: CHANNEL_SEARCH_PAGE_SIZE,
            offset,
            maUploadStatus: 'uploaded',
            chId: channelId ? [channelId] : [],
            ...(searchQuery && { search: searchQuery }),
        },
    });

    const channelSearchQuery = useQuery<GetChannelVideosData, GetChannelVideosVariables>(
        GET_CHANNEL_VIDEOS,
        {
            client: getAuthApolloClient(),
            variables: getSearchVariables(0),
            skip: !channelId || !searchQuery,
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'network-only',
        }
    );

    const searchResults = useMemo(
        () => channelSearchQuery.data?.getMyVideos?.videos || [],
        [channelSearchQuery.data]
    );
    const searchTotal = channelSearchQuery.data?.getMyVideos?.totalCount || 0;
    const searchLoading = channelSearchQuery.loading;

    const canEditOrDelete =
        hasPermission(user, PERMISSIONS.UPDATE_MEDIA) ||
        hasPermission(user, PERMISSIONS.DELETE_MEDIA);

    const handleLoadMoreSearchResults = () => {
        if (searchResults.length >= searchTotal) return;

        channelSearchQuery.fetchMore({
            variables: getSearchVariables(searchResults.length),
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev;
                return {
                    getMyVideos: {
                        ...fetchMoreResult.getMyVideos,
                        videos: [...(prev.getMyVideos?.videos || []), ...(fetchMoreResult.getMyVideos?.videos || [])],
                    },
                };
            },
        });
    };

    if (searchLoading && searchResults.length === 0) {
        return (
            <div className={styles.emptyState}>
                <Spinner />
            </div>
        );
    }

    if (searchQuery && searchResults.length === 0) {
        return (
            <div className={styles.emptyState}>
                <IllustrationIcon className={styles.emptyIcon} aria-hidden="true" />
                <div className={styles.emptyTitle}>{t('channel.no_results')}</div>
                <div className={styles.emptyDescription}>
                    {t('channel.no_results_chennal', { searchQuery, defaultValue: `This channel has no content that matched "${searchQuery}"` })}
                </div>
            </div>
        );
    }

    const isReelMedia = (item: { maType?: string | null }) => {
        const type = (item.maType || '').toLowerCase();
        return type === 'reels' || type === 'reel';
    };

    const handleMediaClick = (e: React.MouseEvent, item: ChannelHomeMedia | ChannelVideo) => {
        e.preventDefault();
        const isReel = isReelMedia(item);
        const returnPathValue = currentLocation || `${location.pathname}${location.search}`;
        setReturnPath(returnPathValue);
        if (isReel) {
            setPreviewUrl(`${env.APP_BASE_URL || ''}${item.maS3MediaKeyUrl}`);
            setValues({
                title: item.maTitle,
                description: item.maDescription || '',
                category: item.maCategory || '',
                tags: Array.isArray(item.maTags) ? item.maTags.join(', ') : typeof item.maTags === 'string' ? item.maTags : '',
            });
            const currentOffset = Math.floor(searchResults.indexOf(item as ChannelVideo) / CHANNEL_REELS_PAGE_SIZE) * CHANNEL_REELS_PAGE_SIZE;
            navigate(`${PATH_CHENNAL_PREVIEW}/${item.maId}?returnPath=${encodeURIComponent(returnPathValue)}&offset=${currentOffset}`);
        } else {
            navigate(`${PATH_MY_VIDEO_DETAILS}/${item.maId}?returnPath=${encodeURIComponent(returnPathValue)}`);
        }
    };

    return (
        <>
            <div className={classNames(styles.grid, styles.videosGrid)}>
                {searchResults.map((item) => {
                    const thumbUrl = `${env.APP_BASE_URL || ''}${item.maS3ThumbnailKeyUrl}`;
                    const isReels = item.maType === 'reels';

                    // Only check if reported (either item property or prop)
                    const itemIsReported = (item as any)?.isReported || propIsReported;

                    return (
                        <div className={styles.videoCard} key={item.maId}>
                            <div
                                className={styles.videoThumbWrap}
                                role="button"
                                tabIndex={0}
                                onClick={(e) => handleMediaClick(e, item)}
                            >
                                <img className={styles.videoThumb} src={thumbUrl} alt={item.maTitle} loading="lazy" />

                                {/* Simple Reported Overlay */}
                                {itemIsReported && (
                                    <div className={styles.statusOverlay}>
                                        <div className={styles.statusContent}>
                                            <Icon icon={InfoIcon} className={styles.statusIcon} />
                                            <span>
                                                {t('video:reel_reported', { defaultValue: 'Content Reported' })}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                <div className={classNames(styles.tags, { [styles.videoDuration]: !isReels })}>
                                    <div className={classNames(styles.tag, { [styles.reelsTag]: isReels })}>
                                        {isReels && <Icon icon={PlayIcon} className={styles.playIcon} />}
                                        {isReels
                                            ? formatTime(Number(item.maDuration) / 1000 || 0)
                                            : formatDurationTag(Number(item.maDuration) / 1000 || 0)}
                                    </div>
                                </div>
                            </div>
                            <div className={styles.cardFooter}>
                                <span className={styles.cardLabel} title={item.maTitle}>
                                    {item.maTitle}
                                </span>
                                {canEditOrDelete && !normalUser && !itemIsReported && (
                                    <button
                                        type="button"
                                        className={styles.cardMenuButton}
                                        aria-label="More options"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onMediaMenuClick?.(e, item);
                                        }}
                                    >
                                        <Icon icon={MoreIcon} />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {searchResults.length < searchTotal && (
                <div className={styles.loadMoreContainer} style={{ textAlign: 'center', marginTop: '32px' }}>
                    <Button
                        label={searchLoading ? 'Loading...' : 'Load more'}
                        onClick={handleLoadMoreSearchResults}
                        disabled={searchLoading}
                        variant="outlined"
                    />
                </div>
            )}
        </>
    );
};

export default ChannelSearchResults;